import type { BalanceTable } from '../core/game';

export const TICK = 1 / 60;

// Tier scheme: tier1 = base. Each upgrade is ~3× stronger; tier2 costs 2× the
// base tower price, tier3 costs 4× the base price (escalating power spike).
export const BALANCE: BalanceTable = {
  towers: {
    patty: {
      tier1: {
        behavior: 'single',
        cost: 100,
        upgradeCost: 0,
        range: 140,
        fireRate: 1.5,
        damage: 12,
        projectileSpeed: 300,
      },
      tier2: {
        behavior: 'single',
        cost: 100,
        upgradeCost: 200,
        range: 150,
        fireRate: 1.5,
        damage: 36,
        projectileSpeed: 300,
      },
      tier3: {
        behavior: 'single',
        cost: 100,
        upgradeCost: 400,
        range: 160,
        fireRate: 1.5,
        damage: 108,
        projectileSpeed: 300,
      },
      // Evolution (player T4): the tier-2 form wreathed in a purple fiery aura.
      tier4: {
        behavior: 'single',
        cost: 100,
        upgradeCost: 800,
        range: 185,
        fireRate: 1, // one shot per second
        damage: 125,
        projectileSpeed: 300,
      },
    },
    // Pickle Sniper — the line-piercer. Pierces even at tier 1 so its niche
    // (long grunt trains) exists the moment Episode II introduces it.
    pickle: {
      tier1: {
        behavior: 'pierce',
        pierce: 2,
        cost: 150,
        upgradeCost: 0,
        range: 320,
        fireRate: 0.5,
        damage: 40,
        projectileSpeed: 620,
      },
      tier2: {
        behavior: 'pierce',
        pierce: 3,
        cost: 150,
        upgradeCost: 300,
        range: 340,
        fireRate: 0.5,
        damage: 120,
        projectileSpeed: 620,
      },
      tier3: {
        behavior: 'pierce',
        pierce: 5,
        cost: 150,
        upgradeCost: 600,
        range: 360,
        fireRate: 0.5,
        damage: 360,
        projectileSpeed: 620,
      },
    },
    cheese: {
      tier1: {
        behavior: 'aura-slow',
        cost: 100,
        upgradeCost: 0,
        range: 110,
        fireRate: 0,
        damage: 0,
        slow: { magnitude: 0.4, duration: 0.4 },
      },
      tier2: {
        behavior: 'aura-slow',
        cost: 100,
        upgradeCost: 240,
        range: 130,
        fireRate: 0,
        damage: 0,
        slow: { magnitude: 0.55, duration: 0.6 },
      },
      tier3: {
        behavior: 'aura-slow',
        cost: 100,
        upgradeCost: 480,
        range: 150,
        fireRate: 0,
        damage: 0,
        slow: { magnitude: 0.75, duration: 0.8 },
      },
      // Evolution (player T4): two mini blue cheeses flank it; the aura now
      // slows enemies by 85% — a crawl through the puddle, not a dead stop, so
      // a wave caught in it still creeps forward instead of parking forever.
      tier4: {
        behavior: 'aura-slow',
        cost: 100,
        upgradeCost: 600,
        range: 150,
        fireRate: 0,
        damage: 0,
        slow: { magnitude: 0.85, duration: 0.8 },
      },
    },
    bacon: {
      tier1: {
        behavior: 'aoe',
        aoeRadius: 60,
        cost: 130,
        upgradeCost: 0,
        range: 120,
        fireRate: 1,
        damage: 14,
        projectileSpeed: 240,
      },
      tier2: {
        behavior: 'aoe',
        aoeRadius: 70,
        cost: 130,
        upgradeCost: 260,
        range: 120,
        fireRate: 1,
        damage: 40,
        projectileSpeed: 240,
        burn: { magnitude: 8, duration: 3 },
      },
      tier3: {
        behavior: 'aoe',
        aoeRadius: 85,
        cost: 130,
        upgradeCost: 520,
        range: 130,
        fireRate: 1,
        damage: 115,
        projectileSpeed: 240,
        burn: { magnitude: 20, duration: 3 },
      },
    },
    sauce: {
      tier1: {
        behavior: 'debuff',
        cost: 110,
        upgradeCost: 0,
        range: 160,
        fireRate: 1.2,
        damage: 6,
        projectileSpeed: 300,
        vulnerable: { magnitude: 0.4, duration: 4 },
      },
      tier2: {
        behavior: 'debuff',
        cost: 110,
        upgradeCost: 220,
        range: 160,
        fireRate: 1.2,
        damage: 18,
        projectileSpeed: 300,
        vulnerable: { magnitude: 0.55, duration: 4 },
        burn: { magnitude: 8, duration: 3 },
      },
      tier3: {
        behavior: 'debuff',
        cost: 110,
        upgradeCost: 440,
        range: 170,
        fireRate: 1.2,
        damage: 54,
        projectileSpeed: 300,
        vulnerable: { magnitude: 0.7, duration: 4 },
        burn: { magnitude: 20, duration: 3 },
      },
    },
    // The Cheese Bladesmen — a melee Hambi that dashes out, cleave-slashes with a
    // cheese sword, returns to base, and waits 1s. Range mirrors the Hambi Tower.
    blade: {
      tier1: {
        behavior: 'melee',
        cost: 115,
        upgradeCost: 0,
        range: 140,
        fireRate: 0,
        damage: 20,
        moveSpeed: 260,
        cleaveRadius: 40,
        strikeWait: 1,
      },
      tier2: {
        behavior: 'melee',
        cost: 115,
        upgradeCost: 215,
        range: 150,
        fireRate: 0,
        damage: 40,
        moveSpeed: 260,
        cleaveRadius: 40,
        strikeWait: 1,
      },
      tier3: {
        behavior: 'melee',
        cost: 115,
        upgradeCost: 450,
        range: 160,
        fireRate: 0,
        damage: 115,
        moveSpeed: 260,
        cleaveRadius: 40,
        strikeWait: 1,
      },
    },
    // BBQ Bomber — a Hambi that lobs exploding bombs across a very large range.
    // Reuses the `aoe` behavior; tier2 throws a staggered twin-bomb salvo, tier3
    // is a slow mega bomb. Boom SFX + explosion play on impact (see game/render).
    bomb: {
      tier1: {
        behavior: 'aoe',
        cost: 150,
        upgradeCost: 0,
        range: 340,
        fireRate: 0.33, // ~3s cooldown (0.33 exactly, so UI never shows 0.3333…)
        damage: 50,
        aoeRadius: 110,
        projectileSpeed: 300,
        salvo: 1,
      },
      tier2: {
        behavior: 'aoe',
        cost: 150,
        upgradeCost: 300,
        range: 360,
        fireRate: 0.25,
        damage: 50,
        aoeRadius: 110,
        projectileSpeed: 300,
        salvo: 2,
        salvoDelay: 0.6,
      },
      tier3: {
        behavior: 'aoe',
        cost: 150,
        upgradeCost: 500,
        range: 380,
        fireRate: 0.17, // ~6s cooldown (0.17 exactly — clean in UI)
        damage: 150,
        aoeRadius: 200,
        projectileSpeed: 300,
        salvo: 1,
      },
      // Evolution (player T4): the tier-3 mega bomb thrown TWICE per volley,
      // on the same ~6s cooldown.
      tier4: {
        behavior: 'aoe',
        cost: 150,
        upgradeCost: 700,
        range: 380,
        fireRate: 0.17,
        damage: 150,
        aoeRadius: 200,
        projectileSpeed: 300,
        salvo: 2,
        salvoDelay: 0.6,
      },
    },
    // Wise Hambi — a meditating Hambi in a bubble. Pure support: deals no damage
    // and never fires. On a repeating cooldown it FREEZES all enemy movement and
    // enemy spawning across the whole map for 5s while every other tower keeps
    // firing. Upgrades only shorten the cooldown (30 → 25 → 20s).
    wise: {
      tier1: {
        behavior: 'global-freeze',
        cost: 500,
        upgradeCost: 0,
        range: 0,
        fireRate: 0,
        damage: 0,
        abilityCooldown: 30,
        freezeDuration: 5,
      },
      tier2: {
        behavior: 'global-freeze',
        cost: 500,
        upgradeCost: 750,
        range: 0,
        fireRate: 0,
        damage: 0,
        abilityCooldown: 25,
        freezeDuration: 5,
      },
      tier3: {
        behavior: 'global-freeze',
        cost: 500,
        upgradeCost: 1000,
        range: 0,
        fireRate: 0,
        damage: 0,
        abilityCooldown: 20,
        freezeDuration: 5,
      },
      // Evolution (player T4): the bubble turns purple and the map-wide freeze
      // recharges in half the time.
      tier4: {
        behavior: 'global-freeze',
        cost: 500,
        upgradeCost: 1000,
        range: 0,
        fireRate: 0,
        damage: 0,
        abilityCooldown: 10,
        freezeDuration: 5,
      },
    },
    // Wizard Tower — Hambi in a wizard hat, won by beating the Null Nugget
    // (Ch2 Ep V). Purple bolt every second plus a big green blast every 15 s.
    wizard: {
      tier1: {
        behavior: 'single',
        cost: 150,
        upgradeCost: 0,
        range: 180,
        fireRate: 1,
        damage: 30,
        projectileSpeed: 300,
        special: { interval: 15, damage: 50 },
      },
      tier2: {
        behavior: 'single',
        cost: 150,
        upgradeCost: 315,
        range: 200,
        fireRate: 1,
        damage: 75,
        projectileSpeed: 300,
        special: { interval: 15, damage: 100 },
      },
      tier3: {
        behavior: 'single',
        cost: 150,
        upgradeCost: 500,
        range: 220,
        fireRate: 1,
        damage: 130,
        projectileSpeed: 300,
        special: { interval: 15, damage: 150 },
      },
    },
    // General Sniper — a sunglasses-wearing Hambi with a long rifle. Longest
    // range in the game, slow heavy single-target bullets. Chapter-2 shop tower.
    sniper: {
      tier1: {
        behavior: 'single',
        cost: 125,
        upgradeCost: 0,
        range: 380,
        fireRate: 0.5,
        damage: 50,
        projectileSpeed: 650,
      },
      tier2: {
        behavior: 'single',
        cost: 125,
        upgradeCost: 225,
        range: 380,
        fireRate: 0.5,
        damage: 100,
        projectileSpeed: 650,
      },
      tier3: {
        behavior: 'single',
        cost: 125,
        upgradeCost: 515,
        range: 380,
        fireRate: 0.5,
        damage: 175,
        projectileSpeed: 650,
      },
    },
    // Dark Hambi — an angry Hambi wreathed in a dark aura. Pure poison: zero
    // direct damage, all damage is the burn DoT. Fires every 0.8s (tier 4 every
    // 1s, but hits harder) — retuned 2026-07-16 from 2s/2s/3s, player-directed.
    // Chapter-2 shop tower.
    darkhambi: {
      tier1: {
        behavior: 'debuff',
        cost: 150,
        upgradeCost: 0,
        range: 200,
        fireRate: 1.25, // one bolt every 0.8s
        damage: 0,
        projectileSpeed: 300,
        burn: { magnitude: 30, duration: 4 },
      },
      tier2: {
        behavior: 'debuff',
        cost: 150,
        upgradeCost: 275,
        range: 200,
        fireRate: 1.25, // one bolt every 0.8s
        damage: 0,
        projectileSpeed: 300,
        burn: { magnitude: 40, duration: 4 },
      },
      tier3: {
        behavior: 'debuff',
        cost: 150,
        upgradeCost: 550,
        range: 200,
        fireRate: 1, // 1s — slower than the lower tiers but the heaviest burn
        damage: 0,
        projectileSpeed: 300,
        burn: { magnitude: 50, duration: 4 },
      },
    },
    // Ninja Hambi — a shop-bought melee brawler (page 2). Patterned on the Cheese
    // Bladesman but lands a 3-hit flurry each strike (50/75/100 per hit) and can
    // never be stunned by the Null-Hex. Range mirrors the Bladesman; it lunges
    // again the instant it returns to its plot (strikeWait 0 — retuned
    // 2026-07-16 from 1s, player-directed), so only the travel time paces it.
    // Place 200, +300, +600.
    ninja: {
      tier1: {
        behavior: 'melee',
        cost: 200,
        upgradeCost: 0,
        range: 140,
        fireRate: 0,
        damage: 50,
        hits: 3,
        moveSpeed: 260,
        cleaveRadius: 40,
        strikeWait: 0,
        stunImmune: true,
      },
      tier2: {
        behavior: 'melee',
        cost: 200,
        upgradeCost: 300,
        range: 150,
        fireRate: 0,
        damage: 75,
        hits: 3,
        moveSpeed: 260,
        cleaveRadius: 40,
        strikeWait: 0,
        stunImmune: true,
      },
      tier3: {
        behavior: 'melee',
        cost: 200,
        upgradeCost: 600,
        range: 160,
        fireRate: 0,
        damage: 100,
        hits: 3,
        moveSpeed: 260,
        cleaveRadius: 40,
        strikeWait: 0,
        stunImmune: true,
      },
      // Evolution (player T4): the aura turns green and the ninja splits into
      // two copies that flurry two enemies at once (see TowerState.melee2).
      tier4: {
        behavior: 'melee',
        cost: 200,
        upgradeCost: 900,
        range: 160,
        fireRate: 0,
        damage: 100,
        hits: 3,
        moveSpeed: 260,
        cleaveRadius: 40,
        strikeWait: 0,
        stunImmune: true,
      },
    },
    // Sensei Ketchup — gifted for beating the Chapter 2 finale. A calm single-
    // target shooter that lobs ketchup every 0.6s (40/75/115 damage).
    // Medium range, growing with each tier. Place 350, +500, +750.
    sensei: {
      tier1: {
        behavior: 'single',
        cost: 350,
        upgradeCost: 0,
        range: 150,
        fireRate: 1 / 0.6, // one shot every 0.6s
        damage: 40,
        projectileSpeed: 320,
      },
      tier2: {
        behavior: 'single',
        cost: 350,
        upgradeCost: 500,
        range: 165,
        fireRate: 1 / 0.6, // one shot every 0.6s
        damage: 75,
        projectileSpeed: 320,
      },
      tier3: {
        behavior: 'single',
        cost: 350,
        upgradeCost: 750,
        range: 180,
        fireRate: 1 / 0.6, // one shot every 0.6s
        damage: 115,
        projectileSpeed: 320,
      },
      // Evolution (player T4): green fiery aura, six orbiting stars, and green
      // spikes — firing a heavier blob every 0.4s.
      tier4: {
        behavior: 'single',
        cost: 350,
        upgradeCost: 1100,
        range: 180,
        fireRate: 1 / 0.4, // one shot every 0.4s
        damage: 150,
        projectileSpeed: 320,
      },
    },
    // Boxer Hambi — a cheeseburger prizefighter in a red headband and boxing
    // gloves. Melee, on the Cheese Bladesman's dash-out / punch / dash-back
    // cycle and its range, but hitting far harder on a much shorter leash: one
    // 100-damage punch, then only 0.3s of rest once he is back on his plot, so
    // the travel out and back is nearly all that paces him. Sold on the
    // temporary third shop page for 1 Hambi coin. Place 150, +350, +450.
    boxer: {
      tier1: {
        behavior: 'melee',
        cost: 150,
        upgradeCost: 0,
        range: 140,
        fireRate: 0,
        damage: 100,
        moveSpeed: 260,
        cleaveRadius: 40,
        strikeWait: 0.3,
      },
      tier2: {
        behavior: 'melee',
        cost: 150,
        upgradeCost: 350,
        range: 150,
        fireRate: 0,
        damage: 150,
        moveSpeed: 260,
        cleaveRadius: 40,
        strikeWait: 0.3,
      },
      tier3: {
        behavior: 'melee',
        cost: 150,
        upgradeCost: 450,
        range: 160,
        fireRate: 0,
        damage: 200,
        moveSpeed: 260,
        cleaveRadius: 40,
        strikeWait: 0.3,
      },
    },
    // Gunner Hambi — a cheeseburger gunslinger with two pistols. One bullet a
    // second, and every bullet rolls its own quality: 65% ordinary, 25% a
    // critical, 10% a headshot. The spread IS the tower, so the good rolls are
    // announced on the enemy as CRITICAL / HEADSHOT bubble text.
    //
    // Deliberately overpowered, player-directed (2026-08-13): expected damage
    // is 172.5/shot at tier 1 and 277.5 at tier 3, all at 1 shot/sec, against
    // the General Sniper's 87.5 DPS — the previous ceiling. That is the point
    // of a 1-coin temporary offer; retuning him means editing this block and
    // nothing else. Shop page 3, alongside Boxer. Place 200, +350, +500.
    gunner: {
      tier1: {
        behavior: 'single',
        cost: 200,
        upgradeCost: 0,
        range: 160,
        fireRate: 1, // one shot per second
        damage: 150,
        projectileSpeed: 700,
        shotRoll: { crit: { chance: 0.25, damage: 200 }, headshot: { chance: 0.1, damage: 250 } },
      },
      tier2: {
        behavior: 'single',
        cost: 200,
        upgradeCost: 350,
        range: 170,
        fireRate: 1,
        damage: 200,
        projectileSpeed: 700,
        shotRoll: { crit: { chance: 0.25, damage: 250 }, headshot: { chance: 0.1, damage: 300 } },
      },
      tier3: {
        behavior: 'single',
        cost: 200,
        upgradeCost: 500,
        range: 180,
        fireRate: 1,
        damage: 250,
        projectileSpeed: 700,
        shotRoll: { crit: { chance: 0.25, damage: 300 }, headshot: { chance: 0.1, damage: 400 } },
      },
    },
    // Fire Mage — a crowned cheeseburger wreathed in fire that hurls fireballs
    // every 1.2s. One shot in four comes out BLUE and hits for more: it reuses
    // the Gunner's `shotRoll`, with the headshot band omitted entirely (there
    // are only two outcomes here), so the blue shot IS the crit band. The tell
    // is the projectile colour plus a BLUE FLAME bubble, not a damage number.
    // Place 250, +350, +500.
    firemage: {
      tier1: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 0,
        range: 250,
        fireRate: 1 / 1.2, // one fireball every 1.2s
        damage: 150,
        projectileSpeed: 420,
        shotRoll: { crit: { chance: 0.25, damage: 175 } },
      },
      tier2: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 350,
        range: 250,
        fireRate: 1 / 1.2,
        damage: 250,
        projectileSpeed: 420,
        shotRoll: { crit: { chance: 0.25, damage: 275 } },
      },
      tier3: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 500,
        range: 250,
        fireRate: 1 / 1.2,
        damage: 300,
        projectileSpeed: 420,
        shotRoll: { crit: { chance: 0.25, damage: 350 } },
      },
    },
    // Aqua Sorcerer — a cheeseburger ringed by orbiting water that lobs a water
    // ball every 1.5s. The droplet bursts on impact and soaks the enemy AHEAD of
    // its target and the one BEHIND for the same damage (`neighborSplash`), so
    // against a packed lane it is worth triple its damage number — which is what
    // pays for the slow cadence. Range matches the Fire Mage.
    // Place 250, +350, +500.
    aquasorcerer: {
      tier1: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 0,
        range: 250,
        fireRate: 1 / 1.5, // one water ball every 1.5s
        damage: 175,
        projectileSpeed: 420,
        neighborSplash: true,
      },
      tier2: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 350,
        range: 250,
        fireRate: 1 / 1.5,
        damage: 275,
        projectileSpeed: 420,
        neighborSplash: true,
      },
      tier3: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 500,
        range: 250,
        fireRate: 1 / 1.5,
        damage: 350,
        projectileSpeed: 420,
        neighborSplash: true,
      },
    },
    // Shrekbook Hambi — a cheeseburger reading a green book, wrapped in a swampy
    // green aura at tier 2 and crowned at tier 3. Mechanically the Hambi Tower's
    // shot (same `single` behavior, same projectile art and fire SFX) with the
    // numbers turned all the way up: 300/450/600 a second at a flat 200 range.
    //
    // Nothing sells him. He is earned in the Test Arena by sending one wave of
    // exactly 100 Shrekbruits and killing every one — see core/arenaChallenge.ts
    // — which is what pays for the strongest single-target damage in the game.
    // Place 150, +200, +300.
    shrekbook: {
      tier1: {
        behavior: 'single',
        cost: 150,
        upgradeCost: 0,
        range: 200,
        fireRate: 1, // one shot per second
        damage: 300,
        projectileSpeed: 300,
      },
      tier2: {
        behavior: 'single',
        cost: 150,
        upgradeCost: 200,
        range: 200,
        fireRate: 1,
        damage: 450,
        projectileSpeed: 300,
      },
      tier3: {
        behavior: 'single',
        cost: 150,
        upgradeCost: 300,
        range: 200,
        fireRate: 1,
        damage: 600,
        projectileSpeed: 300,
      },
    },
    // Phantom Hambi — a translucent, spectral cheeseburger: a blue-glass bun and
    // patty speckled with starlight, one calm smiling face, and a shoal of
    // ghosts orbiting it (white at tier 2, neon green at tier 3). It shoots a
    // ghost at the nearest enemy every 0.8 seconds at every tier.
    //
    // The RED BULLET is TIER 3 ONLY: a maxed Phantom charges for 30 seconds and
    // then hits for a flat 1500 — a hitscan special (the shot cannot whiff)
    // whose paused power-up cinematic the render layer plays off the sim's
    // `specialFired` event. Tiers 1 and 2 have no `special` at all, so they
    // neither fire it nor show the red charge bar the render layer builds from
    // `special.interval`; the burst is what the +400/+600 upgrades buy, on top
    // of the ordinary 325 → 450 → 600 ghost damage.
    //
    // Mechanically the Wizard Tower's shape (`single` + `special`) at a much
    // longer 250 range and a far heavier hand. Nothing sells him: he is the 5%
    // slot in the Restaurant's Normal Takeout Bag (see config/restaurant.ts),
    // which is what pays for the numbers. Place 250, +400, +600.
    phantom: {
      tier1: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 0,
        range: 250,
        fireRate: 1.25, // one ghost every 0.8 seconds
        damage: 325,
        projectileSpeed: 380,
      },
      tier2: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 400,
        range: 250,
        fireRate: 1.25,
        damage: 450,
        projectileSpeed: 380,
      },
      tier3: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 600,
        range: 250,
        fireRate: 1.25,
        damage: 600,
        projectileSpeed: 380,
        special: { interval: 30, damage: 1500, hitscan: true },
      },
    },
    // Lightning Hambi — The Hub's Market (400 Hambi coins; spec Revision 6,
    // retuned in Revision 8).
    // Hambi under his own storm cloud. He never aims: every 5 seconds, while
    // anything is on the track, a lightning cloud sets off from the player's
    // BASE and rolls back up the track against the traffic, striking each
    // enemy it passes for `damage` until it has hit `pierce` (3) of them, or
    // reaches the spawn end and fades. Tiers climb in damage only; `range` is
    // 0 because the whole track is his range (no ring is drawn). Carries 15 HP
    // in The Hub's levels, like every Market Hambi.
    lightning: {
      tier1: {
        behavior: 'path-shot',
        cost: 250,
        upgradeCost: 0,
        range: 0,
        fireRate: 1 / 5,
        damage: 500,
        pierce: 3,
        projectileSpeed: 220,
        hp: 15,
      },
      tier2: {
        behavior: 'path-shot',
        cost: 250,
        upgradeCost: 400,
        range: 0,
        fireRate: 1 / 5,
        damage: 750,
        pierce: 3,
        projectileSpeed: 220,
        hp: 15,
      },
      tier3: {
        behavior: 'path-shot',
        cost: 250,
        upgradeCost: 700,
        range: 0,
        fireRate: 1 / 5,
        damage: 1000,
        pierce: 3,
        projectileSpeed: 220,
        hp: 15,
      },
    },
    // Spear Hambi — The Hub's Market (300 Hambi coins; spec Revision 6). A
    // melee Hambi like Boxer and Ninja: charges the nearest enemy within
    // range of his PLOT (250 / 300 / 350 — far further than the brawlers'
    // 140), lands one spear thrust, and waits 0.4 s back on the plot before
    // the next charge. Carries 15 HP in The Hub's levels.
    spear: {
      tier1: {
        behavior: 'melee',
        cost: 200,
        upgradeCost: 0,
        range: 250,
        fireRate: 0,
        damage: 200,
        moveSpeed: 320,
        cleaveRadius: 40,
        strikeWait: 0.4,
        hp: 15,
      },
      tier2: {
        behavior: 'melee',
        cost: 200,
        upgradeCost: 350,
        range: 300,
        fireRate: 0,
        damage: 300,
        moveSpeed: 320,
        cleaveRadius: 40,
        strikeWait: 0.4,
        hp: 15,
      },
      tier3: {
        behavior: 'melee',
        cost: 200,
        upgradeCost: 500,
        range: 350,
        fireRate: 0,
        damage: 350,
        moveSpeed: 320,
        cleaveRadius: 40,
        strikeWait: 0.4,
        hp: 15,
      },
    },
    // Bow Hambi — The Hub's Market (500 Hambi coins; spec Revision 7, retuned
    // in Revision 8). One
    // arrow a second for 350 at 300 / 350 / 400 range. From tier 2 every
    // arrow has a 25% chance to fly POISONED: no impact damage, 400/s for 3 s
    // (1200) instead. Tier 3 looses two arrows a shot at two different
    // enemies. Carries 15 HP in The Hub's levels.
    bow: {
      tier1: {
        behavior: 'single',
        cost: 300,
        upgradeCost: 0,
        range: 300,
        fireRate: 1,
        damage: 350,
        projectileSpeed: 720,
        hp: 15,
      },
      tier2: {
        behavior: 'single',
        cost: 300,
        upgradeCost: 500,
        range: 350,
        fireRate: 1,
        damage: 350,
        projectileSpeed: 720,
        poisonArrow: { chance: 0.25, magnitude: 400, duration: 3 },
        hp: 15,
      },
      tier3: {
        behavior: 'single',
        cost: 300,
        upgradeCost: 900,
        range: 400,
        fireRate: 1,
        damage: 350,
        projectileSpeed: 720,
        poisonArrow: { chance: 0.25, magnitude: 400, duration: 3 },
        volley: 2,
        hp: 15,
      },
    },
    // ── The Capybara Event's capsule Hambis (spec 2026-09-16) ─────────────
    // Bought nowhere: a capsule on the EVENTS tab is the only way in. All
    // three carry 15 HP in The Hub's levels, like the Market's Hambis.
    //
    // Capy — a plain chestnut capybara who sits on his plot until something
    // comes into range, charges it, lands one bite (a knife from tier 2) and
    // settles back for a full second before the next. The one-second cadence
    // IS `strikeWait`: a melee tower has no fireRate.
    capy: {
      tier1: {
        behavior: 'melee',
        cost: 100,
        upgradeCost: 0,
        range: 100,
        fireRate: 0,
        damage: 100,
        moveSpeed: 320,
        cleaveRadius: 40,
        strikeWait: 1,
        hp: 15,
      },
      tier2: {
        behavior: 'melee',
        cost: 100,
        upgradeCost: 200,
        range: 150,
        fireRate: 0,
        damage: 200,
        moveSpeed: 320,
        cleaveRadius: 40,
        strikeWait: 1,
        hp: 15,
      },
      tier3: {
        behavior: 'melee',
        cost: 100,
        upgradeCost: 300,
        range: 200,
        fireRate: 0,
        damage: 300,
        moveSpeed: 320,
        cleaveRadius: 40,
        strikeWait: 1,
        hp: 15,
      },
    },
    // General Capy — a helmeted capybara who sends a RUNNER up the track every
    // three seconds, the way Lightning Hambi sends a cloud: it sets off from
    // the player's base, runs back against the traffic and bites each enemy it
    // passes until it has hit three. One runner in six or seven (a quarter at
    // tier 3) is a RED imposter worth 750 whatever the tier — rolled in the sim
    // at fire time, from the seeded Rng, so a replay runs identically.
    //
    // The art gives him a scoped rifle from tier 2. He never fires it: the
    // rifle is flavour, the runners are the weapon.
    generalcapy: {
      tier1: {
        behavior: 'path-shot',
        cost: 200,
        upgradeCost: 0,
        range: 0,
        fireRate: 1 / 3,
        damage: 250,
        pierce: 3,
        projectileSpeed: 260,
        spawnRoll: { chance: 0.15, damage: 750 },
        hp: 15,
      },
      tier2: {
        behavior: 'path-shot',
        cost: 200,
        upgradeCost: 400,
        range: 0,
        fireRate: 1 / 3,
        damage: 375,
        pierce: 3,
        projectileSpeed: 260,
        spawnRoll: { chance: 0.15, damage: 750 },
        hp: 15,
      },
      tier3: {
        behavior: 'path-shot',
        cost: 200,
        upgradeCost: 600,
        range: 0,
        fireRate: 1 / 3,
        damage: 500,
        pierce: 3,
        projectileSpeed: 260,
        spawnRoll: { chance: 0.25, damage: 750 },
        hp: 15,
      },
    },
    // Galactic Capy — a capybara framed in a violet portal, throwing one purple
    // fireball a second at the nearest enemy in range. Every five seconds he
    // throws a BLACK one instead-and-as-well: a flat 500 at every tier that
    // also catches the enemy ahead of and the one behind whatever it hits.
    // Only the black one splashes — `neighborSplash` rides under `special`.
    galacticcapy: {
      tier1: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 0,
        range: 150,
        fireRate: 1,
        damage: 300,
        projectileSpeed: 520,
        special: { interval: 5, damage: 500, neighborSplash: true },
        hp: 15,
      },
      tier2: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 500,
        range: 250,
        fireRate: 1,
        damage: 400,
        projectileSpeed: 520,
        special: { interval: 5, damage: 500, neighborSplash: true },
        hp: 15,
      },
      tier3: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 800,
        range: 350,
        fireRate: 1,
        damage: 500,
        projectileSpeed: 520,
        special: { interval: 5, damage: 500, neighborSplash: true },
        hp: 15,
      },
    },
    // ── Update 1.2's four capsule Hambis (spec 2026-09-17) ────────────────
    // Bought nowhere either: the same capsule is the only way in, and all four
    // carry 15 HP in The Hub's levels like the first three.
    //
    // Hog Hambi — a capybara with a burger riding on his back, who charges the
    // nearest enemy in range, lands one bite and settles back. Mechanically the
    // Capy over again, only harder-hitting, longer-reaching and a touch slower:
    // the 1.2s cadence IS `strikeWait`, because a melee tower has no fireRate.
    // He mirrors like the Capy when the enemy is on the other side.
    hog: {
      tier1: {
        behavior: 'melee',
        cost: 200,
        upgradeCost: 0,
        range: 150,
        fireRate: 0,
        damage: 200,
        moveSpeed: 320,
        cleaveRadius: 40,
        strikeWait: 1.2,
        hp: 15,
      },
      tier2: {
        behavior: 'melee',
        cost: 200,
        upgradeCost: 300,
        range: 200,
        fireRate: 0,
        damage: 300,
        moveSpeed: 320,
        cleaveRadius: 40,
        strikeWait: 1.2,
        hp: 15,
      },
      tier3: {
        behavior: 'melee',
        cost: 200,
        upgradeCost: 500,
        range: 250,
        fireRate: 0,
        damage: 400,
        moveSpeed: 320,
        cleaveRadius: 40,
        strikeWait: 1.2,
        hp: 15,
      },
    },
    // Mage Capy — a capybara in a starry wizard hat who fires a LASER BEAM at
    // the nearest enemy in range every 1.5 seconds, and every 1.0 at tier 3.
    // The beam is render-only (`beamShot`): the sim spawns an ordinary, very
    // fast projectile and the view draws it as a fading line, the same trick
    // the Laser Nacho uses from the other side. The beam is RED at tiers 1 and
    // 2 and GREEN at tier 3 — which is exactly what the art does with the gem
    // on his staff, so the colour always matches the sprite holding it.
    magecapy: {
      tier1: {
        behavior: 'single',
        cost: 150,
        upgradeCost: 0,
        range: 200,
        fireRate: 1 / 1.5,
        damage: 250,
        projectileSpeed: 2400,
        beamShot: true,
        hp: 15,
      },
      tier2: {
        behavior: 'single',
        cost: 150,
        upgradeCost: 250,
        range: 250,
        fireRate: 1 / 1.5,
        damage: 300,
        projectileSpeed: 2400,
        beamShot: true,
        hp: 15,
      },
      tier3: {
        behavior: 'single',
        cost: 150,
        upgradeCost: 450,
        range: 300,
        fireRate: 1,
        damage: 350,
        projectileSpeed: 2400,
        beamShot: true,
        hp: 15,
      },
    },
    // Mini Gunner — a furious capybara behind a six-barrelled minigun, firing
    // one bullet a second at the nearest enemy in range. One shot in five is a
    // HEADSHOT, with the floating text the Gunner Hambi made its own.
    //
    // He declares `headshot` and NO `crit` band — his two outcomes are headshot
    // or ordinary, and a 0%-chance crit would print as "0% crit" everywhere the
    // spec is described. Note the tier-2 headshot is only 25 above his ordinary
    // shot: that is the damage table as specified, and it means tier 2 buys
    // range and base damage rather than a better headshot.
    minigunner: {
      tier1: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 0,
        range: 250,
        fireRate: 1,
        damage: 275,
        projectileSpeed: 700,
        shotRoll: { headshot: { chance: 0.2, damage: 350 } },
        hp: 15,
      },
      tier2: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 350,
        range: 300,
        fireRate: 1,
        damage: 325,
        projectileSpeed: 700,
        shotRoll: { headshot: { chance: 0.2, damage: 350 } },
        hp: 15,
      },
      tier3: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 550,
        range: 350,
        fireRate: 1,
        damage: 375,
        projectileSpeed: 700,
        shotRoll: { headshot: { chance: 0.2, damage: 425 } },
        hp: 15,
      },
    },
    // King Capy — the rarest thing in the capsule. Every 1.2 seconds he sends
    // out UP TO three red gems, one each at three DIFFERENT enemies in range
    // (`volleyDistinct`): a gem with no enemy of its own is not fired, so one
    // enemy in range takes one gem, not three. At tier 3 each gem rolls its own
    // 20% chance of coming out BLUE for a flat 700 — a `shotRoll` with only the
    // crit band, exactly as the Fire Mage's blue fireball is. Tiers 1 and 2
    // declare no `shotRoll` at all and so take no draw from the RNG whatsoever.
    //
    // And when he is destroyed he does not go quietly: `deathBurst` sends five
    // parting gems at five random living enemies anywhere on the map.
    kingcapy: {
      tier1: {
        behavior: 'single',
        cost: 300,
        upgradeCost: 0,
        range: 300,
        fireRate: 1 / 1.2,
        damage: 400,
        projectileSpeed: 560,
        volley: 3,
        volleyDistinct: true,
        deathBurst: { count: 5 },
        hp: 15,
      },
      tier2: {
        behavior: 'single',
        cost: 300,
        upgradeCost: 500,
        range: 300,
        fireRate: 1 / 1.2,
        damage: 500,
        projectileSpeed: 560,
        volley: 3,
        volleyDistinct: true,
        deathBurst: { count: 5 },
        hp: 15,
      },
      tier3: {
        behavior: 'single',
        cost: 300,
        upgradeCost: 1000,
        range: 360,
        fireRate: 1 / 1.2,
        damage: 600,
        projectileSpeed: 560,
        volley: 3,
        volleyDistinct: true,
        shotRoll: { crit: { chance: 0.2, damage: 700 } },
        deathBurst: { count: 5 },
        hp: 15,
      },
    },
    // King Hambi (spec 2026-09-22) — the campaign's crown, won by beating King
    // Ember in The Ember and Flame. A medium yellow shard a second (400 / 550 /
    // 650, range 200 / 235 / 260), and each shard has a 25% chance to SPLIT and
    // hit a second enemy (`splitShot`). His K–O (`shardRain`): every 30 s — 20 s
    // at tier 3 — the game stops, he grows to fill the screen between a giant K
    // and O, floats away to the top, and a rain of shards lands 3000 on EVERY
    // enemy alive. $200 to place, $500 and $1050 to upgrade.
    kinghambi: {
      tier1: {
        behavior: 'single',
        cost: 200,
        upgradeCost: 0,
        range: 200,
        fireRate: 1,
        damage: 400,
        projectileSpeed: 620,
        splitShot: { chance: 0.25 },
        shardRain: { interval: 30, damage: 3000, delay: 1.2 },
      },
      tier2: {
        behavior: 'single',
        cost: 200,
        upgradeCost: 500,
        range: 235,
        fireRate: 1,
        damage: 550,
        projectileSpeed: 620,
        splitShot: { chance: 0.25 },
        shardRain: { interval: 30, damage: 3000, delay: 1.2 },
      },
      tier3: {
        behavior: 'single',
        cost: 200,
        upgradeCost: 1050,
        range: 260,
        fireRate: 1,
        damage: 650,
        projectileSpeed: 620,
        splitShot: { chance: 0.25 },
        shardRain: { interval: 20, damage: 3000, delay: 1.2 },
      },
    },
    // ── The Golden Takeout Bag's three Hambis (spec 2026-09-26) ────────────
    // Bought nowhere: a Golden Takeout Bag is the only way in, one in three
    // each. All three carry 15 HP in The Hub's levels like the capsule towers.
    //
    // Dagger Hambi (Uncommon) — melee: dashes at the nearest enemy within
    // 100 / 125 / 150 px, stabs for 200 / 250 / 300, and is ready again half a
    // second after he gets home (`strikeWait: 0.5`). $170, then $250 and $300.
    dagger: {
      tier1: {
        behavior: 'melee',
        cost: 170,
        upgradeCost: 0,
        range: 100,
        fireRate: 0,
        damage: 200,
        moveSpeed: 420,
        cleaveRadius: 30,
        strikeWait: 0.5,
        hp: 15,
      },
      tier2: {
        behavior: 'melee',
        cost: 170,
        upgradeCost: 250,
        range: 125,
        fireRate: 0,
        damage: 250,
        moveSpeed: 420,
        cleaveRadius: 30,
        strikeWait: 0.5,
        hp: 15,
      },
      tier3: {
        behavior: 'melee',
        cost: 170,
        upgradeCost: 300,
        range: 150,
        fireRate: 0,
        damage: 300,
        moveSpeed: 420,
        cleaveRadius: 30,
        strikeWait: 0.5,
        hp: 15,
      },
    },
    // Leafstorm Hambi (Rare) — a spinning tornado of leaves every second that
    // lands on its target and whirls into the 4 enemies nearest where it lands
    // (`tornado`), 250 / 300 / 350 to each. Range 150 / 200 / 225. $250, then
    // $300 and $400.
    leafstorm: {
      tier1: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 0,
        range: 150,
        fireRate: 1,
        damage: 250,
        projectileSpeed: 420,
        tornado: { targets: 4 },
        hp: 15,
      },
      tier2: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 300,
        range: 200,
        fireRate: 1,
        damage: 300,
        projectileSpeed: 420,
        tornado: { targets: 4 },
        hp: 15,
      },
      tier3: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 400,
        range: 225,
        fireRate: 1,
        damage: 350,
        projectileSpeed: 420,
        tornado: { targets: 4 },
        hp: 15,
      },
    },
    // Samurai Slicer (Epic) — the first MULTI-ATTACKER. Melee: a slash for
    // 300 / 350 / 400 every second inside his INNER ring (`range`, 150 / 175 /
    // 200). Half a second after each slash lands he rolls 40% to throw 2 small
    // daggers at 2 different enemies inside his OUTER ring (`midStrike.range`,
    // 175 / 200 / 225) for 200 each at every tier. His prices were not given
    // in the brief; $300, then $400 and $500 sits him between Leafstorm (Rare)
    // and Mini Gunner (Epic, $250 + 350/550).
    samurai: {
      tier1: {
        behavior: 'melee',
        cost: 300,
        upgradeCost: 0,
        range: 150,
        fireRate: 0,
        damage: 300,
        moveSpeed: 380,
        cleaveRadius: 30,
        strikeWait: 1,
        projectileSpeed: 700,
        midStrike: { delay: 0.5, chance: 0.4, count: 2, damage: 200, range: 175 },
        hp: 15,
      },
      tier2: {
        behavior: 'melee',
        cost: 300,
        upgradeCost: 400,
        range: 175,
        fireRate: 0,
        damage: 350,
        moveSpeed: 380,
        cleaveRadius: 30,
        strikeWait: 1,
        projectileSpeed: 700,
        midStrike: { delay: 0.5, chance: 0.4, count: 2, damage: 200, range: 200 },
        hp: 15,
      },
      tier3: {
        behavior: 'melee',
        cost: 300,
        upgradeCost: 500,
        range: 200,
        fireRate: 0,
        damage: 400,
        moveSpeed: 380,
        cleaveRadius: 30,
        strikeWait: 1,
        projectileSpeed: 700,
        midStrike: { delay: 0.5, chance: 0.4, count: 2, damage: 200, range: 225 },
        hp: 15,
      },
    },
    // ── The Golden Takeout Bag's three more (spec 2026-09-29) ───────────────
    // Mayo Masher (Legendary) — a sludge of mayo at one enemy every second,
    // 300 / 400 / 500, range 150 / 200 / 250, and he can never be stunned.
    // $300, then $500 and $1000.
    masher: {
      tier1: {
        behavior: 'single',
        cost: 300,
        upgradeCost: 0,
        range: 150,
        fireRate: 1,
        damage: 300,
        projectileSpeed: 520,
        stunImmune: true,
        hp: 15,
      },
      tier2: {
        behavior: 'single',
        cost: 300,
        upgradeCost: 500,
        range: 200,
        fireRate: 1,
        damage: 400,
        projectileSpeed: 520,
        stunImmune: true,
        hp: 15,
      },
      tier3: {
        behavior: 'single',
        cost: 300,
        upgradeCost: 1000,
        range: 250,
        fireRate: 1,
        damage: 500,
        projectileSpeed: 520,
        stunImmune: true,
        hp: 15,
      },
    },
    // Grease Goddess (Mythical) — no damage. Her grease
    // slows enemies in range by 50 / 65 / 85% like the Cheese Guardian, and
    // every OTHER tower in her range deals +25% (`allyBoost`, never stacked).
    // Range 140 / 180 / 200. $300, then $600 and $1100.
    grease: {
      tier1: {
        behavior: 'aura-slow',
        cost: 300,
        upgradeCost: 0,
        range: 140,
        fireRate: 0,
        damage: 0,
        slow: { magnitude: 0.5, duration: 0.4 },
        allyBoost: { damage: 0.25 },
        hp: 15,
      },
      tier2: {
        behavior: 'aura-slow',
        cost: 300,
        upgradeCost: 600,
        range: 180,
        fireRate: 0,
        damage: 0,
        slow: { magnitude: 0.65, duration: 0.6 },
        allyBoost: { damage: 0.25 },
        hp: 15,
      },
      tier3: {
        behavior: 'aura-slow',
        cost: 300,
        upgradeCost: 1100,
        range: 200,
        fireRate: 0,
        damage: 0,
        slow: { magnitude: 0.85, duration: 0.8 },
        allyBoost: { damage: 0.25 },
        hp: 15,
      },
    },
    // Ultra Hammy (Ketchuptastic) — FOUR tiers with no Evolution
    // (`nativeTier4`). A blue fireball, then dynamite (tiers 2–3), then purple
    // crystals: 350 / 450 / 550 / 700 a second, range 200 / 250 / 300 / 300.
    // Only one at a time (`unique`). At tier 4, END THEM every 60 s: 5000 on
    // every enemy, +25% on every tower for 20 s, and three tier-2 minions at
    // the top of the screen throwing dynamite for 300 every 0.5 s.
    // $400, then $650, $900 and $1200.
    ultrahammy: {
      tier1: {
        behavior: 'single',
        cost: 400,
        upgradeCost: 0,
        range: 200,
        fireRate: 1,
        damage: 350,
        projectileSpeed: 600,
        unique: true,
        nativeTier4: true,
        hp: 15,
      },
      tier2: {
        behavior: 'single',
        cost: 400,
        upgradeCost: 650,
        range: 250,
        fireRate: 1,
        damage: 450,
        projectileSpeed: 600,
        unique: true,
        hp: 15,
      },
      tier3: {
        behavior: 'single',
        cost: 400,
        upgradeCost: 900,
        range: 300,
        fireRate: 1,
        damage: 550,
        projectileSpeed: 600,
        unique: true,
        hp: 15,
      },
      tier4: {
        behavior: 'single',
        cost: 400,
        upgradeCost: 1200,
        range: 300,
        fireRate: 1,
        damage: 700,
        projectileSpeed: 600,
        unique: true,
        endThem: {
          interval: 60,
          damage: 5000,
          delay: 1.2,
          boost: 0.25,
          duration: 20,
          minions: { count: 3, interval: 0.5, damage: 300, speed: 700 },
        },
        hp: 15,
      },
    },
    // ── The Origin Event's Dark Shop (spec 2026-10-12) ─────────────────────
    // Sold for Dark Hambi Coins in the Dark Shop and nowhere else; on the
    // roster once bought. Every combat stat is the brief's; the $ prices (place
    // / tier 2 / tier 3) were not given and are the agreed suggestion.
    //
    // Horror Hambi: dark red poison balls at 2 DIFFERENT enemies. A ball lands
    // for nothing and poisons instead (Bow Hambi's `poisonArrow`, at 100%):
    // 350/s for 2 s, 400/s at tier 2 and 3; tier 3 fires every 1 s, not 1.5.
    // Poison does not stack — a second ball refreshes it (`applyStatus` keeps
    // the larger dose and the longer clock).
    horror: {
      tier1: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 0,
        range: 150,
        fireRate: 0.67, // ~1.5 s (0.67, so the UI never shows 0.6667)
        damage: 0,
        projectileSpeed: 560,
        poisonArrow: { chance: 1, magnitude: 350, duration: 2 },
        volley: 2,
        volleyDistinct: true,
        hp: 15,
      },
      tier2: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 350,
        range: 200,
        fireRate: 0.67,
        damage: 0,
        projectileSpeed: 560,
        poisonArrow: { chance: 1, magnitude: 400, duration: 2 },
        volley: 2,
        volleyDistinct: true,
        hp: 15,
      },
      tier3: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 500,
        range: 200,
        fireRate: 1,
        damage: 0,
        projectileSpeed: 560,
        poisonArrow: { chance: 1, magnitude: 400, duration: 2 },
        volley: 2,
        volleyDistinct: true,
        hp: 15,
      },
    },
    // Demonic Hambi: flaming spinning skulls that blow up like the BBQ
    // Bomber's bomb (dark purple, not orange) — the full damage on every enemy
    // in the bomb's tier-1 blast, 110 px, at every tier.
    demonic: {
      tier1: {
        behavior: 'aoe',
        cost: 300,
        upgradeCost: 0,
        range: 250,
        fireRate: 0.83, // ~1.2 s
        damage: 300,
        aoeRadius: 110,
        projectileSpeed: 480,
        hp: 15,
      },
      tier2: {
        behavior: 'aoe',
        cost: 300,
        upgradeCost: 400,
        range: 300,
        fireRate: 0.83,
        damage: 400,
        aoeRadius: 110,
        projectileSpeed: 480,
        hp: 15,
      },
      tier3: {
        behavior: 'aoe',
        cost: 300,
        upgradeCost: 550,
        range: 350,
        fireRate: 0.83,
        damage: 495,
        aoeRadius: 110,
        projectileSpeed: 480,
        hp: 15,
      },
    },
    // The Ham Reaper: dark flaming skulls once a second, and the SCYTHE — every
    // 25 s (20 s at tier 3) a huge scythe crosses the whole screen right to
    // left and deals 2000 to every enemy it passes (`scytheSweep`).
    hamreaper: {
      tier1: {
        behavior: 'single',
        cost: 450,
        upgradeCost: 0,
        range: 150,
        fireRate: 1,
        damage: 400,
        projectileSpeed: 640,
        scytheSweep: { interval: 25, damage: 2000, duration: 1.2 },
        hp: 15,
      },
      tier2: {
        behavior: 'single',
        cost: 450,
        upgradeCost: 550,
        range: 200,
        fireRate: 1,
        damage: 500,
        projectileSpeed: 640,
        scytheSweep: { interval: 25, damage: 2000, duration: 1.2 },
        hp: 15,
      },
      tier3: {
        behavior: 'single',
        cost: 450,
        upgradeCost: 750,
        range: 250,
        fireRate: 1,
        damage: 650,
        projectileSpeed: 640,
        scytheSweep: { interval: 20, damage: 2000, duration: 1.2 },
        hp: 15,
      },
    },
    // ── The Origin Event's Light Shop (spec 2026-10-13) ────────────────────
    // Sold for Light Hambi Coins in the Light Shop and nowhere else; on the
    // roster once bought. Every number is the brief's.
    //
    // Healer Hambi: no attack. Every 10 s (7 s at tier 2, 5 s at tier 3) he
    // heals every tower in his range — himself too — by 2 HP (`healPulse`).
    // Range 100 / 150 / 200. $250, then $400 and $600.
    healer: {
      tier1: {
        behavior: 'aura-slow',
        cost: 250,
        upgradeCost: 0,
        range: 100,
        fireRate: 0,
        damage: 0,
        healPulse: { interval: 10, amount: 2 },
        hp: 15,
      },
      tier2: {
        behavior: 'aura-slow',
        cost: 250,
        upgradeCost: 400,
        range: 150,
        fireRate: 0,
        damage: 0,
        healPulse: { interval: 7, amount: 2 },
        hp: 15,
      },
      tier3: {
        behavior: 'aura-slow',
        cost: 250,
        upgradeCost: 600,
        range: 200,
        fireRate: 0,
        damage: 0,
        healPulse: { interval: 5, amount: 2 },
        hp: 15,
      },
    },
    // Banking Burger: no attack. Every enemy killed anywhere pays 200 / 300 /
    // 500 cash on top of its bounty (`killBonus`). One at a time (`unique`), so
    // the bonus never doubles. $300, then $600 and $1200.
    banker: {
      tier1: {
        behavior: 'aura-slow',
        cost: 300,
        upgradeCost: 0,
        range: 0,
        fireRate: 0,
        damage: 0,
        killBonus: { cash: 200 },
        unique: true,
        hp: 15,
      },
      tier2: {
        behavior: 'aura-slow',
        cost: 300,
        upgradeCost: 600,
        range: 0,
        fireRate: 0,
        damage: 0,
        killBonus: { cash: 300 },
        unique: true,
        hp: 15,
      },
      tier3: {
        behavior: 'aura-slow',
        cost: 300,
        upgradeCost: 1200,
        range: 0,
        fireRate: 0,
        damage: 0,
        killBonus: { cash: 500 },
        unique: true,
        hp: 15,
      },
    },
    // Angelic Hambi: a yellow-white angelic fireball once a second for 400 /
    // 550 / 650, range 150 / 200 / 250. At tier 3 each fireball rolls 15% to be
    // a CRITICAL for a flat 700 — a `shotRoll` on tier 3 ONLY, so tiers 1–2
    // take no draw from the RNG. $350, then $650 and $1300.
    angelic: {
      tier1: {
        behavior: 'single',
        cost: 350,
        upgradeCost: 0,
        range: 150,
        fireRate: 1,
        damage: 400,
        projectileSpeed: 620,
        hp: 15,
      },
      tier2: {
        behavior: 'single',
        cost: 350,
        upgradeCost: 650,
        range: 200,
        fireRate: 1,
        damage: 550,
        projectileSpeed: 620,
        hp: 15,
      },
      tier3: {
        behavior: 'single',
        cost: 350,
        upgradeCost: 1300,
        range: 250,
        fireRate: 1,
        damage: 650,
        projectileSpeed: 620,
        shotRoll: { crit: { chance: 0.15, damage: 700 } },
        hp: 15,
      },
    },
    // The Golden Shroom (spec 2026-10-27) — Pathway of the Fungi's prize, the
    // Redshroom with a bronze / silver / gold cap. A golden shroom a second at
    // one enemy (250 / 350 / 500, range 150 / 150 / 200). SHROOM FRENZY: every
    // 25 s the game stops for a 5 s cutscene, then 900 lands on EVERY enemy
    // alive (the K–O's rules, its own events). $250, then $400 and $600 —
    // assumed: the brief gave no prices or cadence.
    goldenshroom: {
      tier1: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 0,
        range: 150,
        fireRate: 1,
        damage: 250,
        projectileSpeed: 620,
        shroomFrenzy: { interval: 25, damage: 900, delay: 0.4 },
      },
      tier2: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 400,
        range: 150,
        fireRate: 1,
        damage: 350,
        projectileSpeed: 620,
        shroomFrenzy: { interval: 25, damage: 900, delay: 0.4 },
      },
      tier3: {
        behavior: 'single',
        cost: 250,
        upgradeCost: 600,
        range: 200,
        fireRate: 1,
        damage: 500,
        projectileSpeed: 620,
        shroomFrenzy: { interval: 25, damage: 900, delay: 0.4 },
      },
    },
  },
  enemies: {
    // Rebalance (2026-07-04 spec): levels start nearly broke, so bounties are
    // the economy — each kill meaningfully funds the build. The Golden Nugget
    // is tuned to be beatable-but-epic with the full Chapter 1 roster (the
    // balance bracket proves it).
    grunt: { hp: 105, speed: 60, bounty: 12 },
    dino: { hp: 85, speed: 90, bounty: 14 },
    fry: { hp: 36, speed: 75, bounty: 5 },
    hotdog: { hp: 700, speed: 40, bounty: 40 },
    // The Golden Nugget must be KILLED — a leak drains all 5 lives and ends
    // the run. 6000 HP is paired with Episode V's fat $2000 opening budget
    // (spec §4e): a competent build still fells it, a naive one doesn't.
    boss: { hp: 6000, speed: 35, bounty: 200, livesCost: 5 },
    // Chapter 2 — Frozen Nugget: a snow-covered nugget, slower and far tankier
    // than the basic foes, worth a fat bounty for the effort it takes to melt.
    frost: { hp: 200, speed: 28, bounty: 55 },
    // Chapter 2 — Frozen Dino: the Dino Scout sealed inside an ice cube,
    // sliding around the track. A touch tougher and faster than the bare dino.
    icedino: { hp: 120, speed: 100, bounty: 20 },
    // Chapter 2 — Dripstone Driller: the Hotdog Brute reborn in the gem cave,
    // a faceted amethyst drill for a head and a sapphire set in its body. The
    // toughest non-boss wall in the campaign; slow, and worth a rich bounty.
    driller: { hp: 900, speed: 40, bounty: 65 },
    // Chapter 2 boss — The Null Nugget: an angry charcoal-and-grey nugget in a
    // wizard hat. Must be KILLED — a leak drains every remaining life (99 >>
    // any startLives) and ends the run. Its Null Hex stuns one random tower
    // for 3s every 10s (see GameSim's stun pass).
    nullnugget: {
      hp: 8000,
      speed: 30,
      bounty: 500,
      livesCost: 99,
      stun: { duration: 3, cooldown: 10 },
    },
    // Chapter 2 — The Ultra Nugget: a nugget encrusted with glowing orange and
    // red mine crystals. A slow crystal wall that drains TWO lives on a leak —
    // on a 2-life Chapter 2 episode a single leak ends the run.
    ultranugget: { hp: 950, speed: 30, bounty: 100, livesCost: 2 },
    // Chapter 2 — The Nugget Jockey: a cave spider ridden by a spear-carrying
    // nugget. Grunt-speed with hotdog-plus HP — a fast tank that punishes thin
    // coverage.
    jockey: { hp: 800, speed: 60, bounty: 75 },
    // Chapter 2 — The Crystal Watcher: a huge levitating amethyst crystal with
    // a nugget riding it, drifting slowly above the track. Carries the Null
    // Nugget's hex — it stuns one random tower for 3s every 10s — making it a
    // creeping threat that must die before it jams the whole defense.
    watcher: {
      hp: 1000,
      speed: 20,
      bounty: 200,
      stun: { duration: 3, cooldown: 10 },
    },
    // Chapter 2 · Episode IX — The Nugget Guard: a nugget in a warrior helmet
    // swinging a broadsword, posted at the castle gate. A brisk, hard-hitting
    // wall that drains TWO lives on a leak — only mustered from wave 8 on.
    nuggetguard: { hp: 1200, speed: 80, bounty: 80, livesCost: 2 },
    // Chapter 2 · Episode X — King Nugget-tron: the crowned nugget king and the
    // final boss of Chapter 2. Must be KILLED — a leak drains every remaining
    // life (99 >> any startLives) and ends the run. Cannot be slowed by any
    // effect (slow auras and the Wise-Hambi freeze both wash off), and his royal
    // decree stuns one random tower for 5s every 8s.
    kingnuggetron: {
      hp: 15000,
      speed: 22,
      bounty: 600,
      livesCost: 99,
      slowImmune: true,
      stun: { duration: 5, cooldown: 8 },
    },
    // Chapter 3 · Episode I — The Carrot Crusher: an orange carrot brandishing a
    // grey sword. A fast, tanky rusher that opens the Hambi Castle assault.
    carrotcrusher: { hp: 1000, speed: 100, bounty: 150 },
    // Chapter 3 · Episode III — The Carrot Ninja: a carrot with an oversized
    // stem, a headband and twin swords. A Carrot Crusher that costs more to
    // kill while keeping the rusher's speed — no special ability. Bounty 180
    // interpolates the veggie roster by toughness (crusher 150 → brocco 200).
    carrotninja: { hp: 1050, speed: 100, bounty: 180 },
    // Chapter 3 · Episode I — The Brocco: a broccoli with baleful red eyes. Its
    // Veggie Hex slows one random tower's fire rate by 50% for 5s every 15s (no
    // tower is immune — see GameSim's hex pass).
    brocco: {
      hp: 1050,
      speed: 80,
      bounty: 200,
      fireRateHex: { slow: 0.5, duration: 5, cooldown: 15 },
    },
    // Chapter 3 · Episode I — The Potato Slicer: a potato wielding a chopping
    // knife and a hammer. The tankiest of the castle roster; headlines the
    // late waves.
    potatoslicer: { hp: 1200, speed: 80, bounty: 230 },
    // Chapter 3 · Episode IV — The Dry Carrot: a beige, dust-caked carrot gone
    // rotten in the Soy Sauce Desert. Faster than the Ninja with a touch more
    // HP, and it carries a short Null-style hex: one random tower is disabled
    // for 2s every 10s. Bounty tops the veggie line for the extra menace.
    drycarrot: {
      hp: 1100,
      speed: 105,
      bounty: 220,
      stun: { duration: 2, cooldown: 10 },
    },
    // Chapter 3 · Episode V — The Mini Bell: a mini red bell pepper with red
    // eyes, called up one at a time from the temple entrance by Emperor Pepro
    // (see the emperor's `summon`). A brisk lone runner (speed 100) — small
    // bounty, since the emperor would otherwise fund the defense sent at him.
    minibell: { hp: 400, speed: 100, bounty: 25 },
    // Chapter 3 boss — Emperor Pepro: a red pepper with dark eyes and a fiery
    // aura, floating on a sandy cloud. Must be KILLED — a leak drains every
    // remaining life. Cannot be slowed, and his Imperial Decree seals tower
    // placement for 10s every 15s. His Mini Bell escort comes ONE at a time
    // from the temple entrance every 5s (only one alive at once).
    emperorpepro: {
      hp: 20000,
      speed: 70,
      bounty: 700,
      livesCost: 99,
      slowImmune: true,
      placementLock: { duration: 10, cooldown: 15 },
      summon: { enemyType: 'minibell', count: 1, cooldown: 5, maxAlive: 1, fromBase: true },
    },
    // Chapter 3 · Episode VI — Caulifire: a cauliflower charred coal-black,
    // wreathed in a red aura, born of the lava pool. No ability — a pure
    // late-wave wall: the toughest veggie yet at a speed that still presses,
    // with the chapter's richest non-boss bounty. Only mustered from wave 10.
    caulifire: { hp: 1400, speed: 80, bounty: 350 },
    // Chapter 3 · Episode VII — The Esparagus Mage: a spear of asparagus in a
    // wizard hat with purple stars orbiting it. Its hex hits HARDER than the
    // Null Nugget's: TWO random towers go dark at once (see `count`), for 2s
    // every 10s. Short lockout, doubled reach — thin defenses lose two guns of
    // coverage at a stroke. Only mustered from wave 9.
    esparagusmage: {
      hp: 1500,
      speed: 75,
      bounty: 450,
      stun: { duration: 2, cooldown: 10, count: 2, shared: true },
    },
    // Chapter 3 · Episode VIII — The Ocraa: a pod of okra kitted out as a
    // viking, horned helmet and spear. No ability — a brisk mid-weight wall
    // that sits between the Dry Carrot and Caulifire on toughness, priced like
    // the Potato Slicer it replaces on the dump's roster. Only mustered from
    // wave 10.
    ocraa: { hp: 1300, speed: 90, bounty: 230 },
    // Chapter 3 · Episode IX — The Bamboo Crew: three stalks of bamboo fighting
    // as one, a sword apiece. The toughest non-boss in the campaign, and it
    // takes YOUR HANDS away rather than a tower: its dojo lockdown seals tower
    // placement for 3s every 15s (the Emperor's decree, on a short leash).
    // Only mustered from wave 9.
    bamboocrew: {
      hp: 1550,
      speed: 80,
      bounty: 480,
      placementLock: { duration: 3, cooldown: 15 },
    },
    // Chapter 3 · Episode X — the Pepro Herald: Emperor Pepro riding ahead of
    // King Ember's army (same sprite — it IS him), sent on waves 5, 10 and 15
    // as a checkpoint rather than a finale. Half the emperor's HP, and he
    // carries NO Imperial Decree here: your hands stay free. Still slow-immune,
    // still escorted by one Mini Bell at a time from the cave mouth. A leak
    // costs two lives — punishing, but it does not end the run the way the
    // emperor's own finale leak does.
    peproherald: {
      hp: 10000,
      speed: 70,
      bounty: 400,
      livesCost: 2,
      slowImmune: true,
      summon: { enemyType: 'minibell', count: 1, cooldown: 5, maxAlive: 1, fromBase: true },
    },
    // Chapter 3 · Episode X — KING EMBER, the final boss of the campaign: a
    // blue flame king in a gold crown, twin ice swords raised, riding a floating
    // purple cloud.
    // Must be KILLED — a leak drains every remaining life (99 >> any
    // startLives). Cannot be slowed. His Ember Barrage is the only enemy
    // ability in the game that DESTROYS towers: from the moment he appears
    // every tower has 3 HP, and every 5s a fireball takes one HP off a random
    // tower (see GameSim's fireball pass).
    kingember: {
      hp: 30000,
      speed: 80,
      bounty: 1200,
      livesCost: 99,
      slowImmune: true,
      towerFire: { damage: 1, cooldown: 5, travel: 0.9 },
    },
    // The Shrekbruit — the Hotdog Brute recoloured entirely in shades of green.
    // TEST ARENA ONLY: it appears in no campaign wave and never will (see
    // config/testArena.ts, which appends it to the arena selector by hand). It
    // exists to be farmed: kill 100 of them in a single arena wave and Shrekbook
    // Hambi unlocks. Half the brute's HP at double its pace, and no ability.
    // The bounty matches the brute's and is inert in practice — the arena is
    // freeBuild, so arena cash buys nothing.
    shrekbruit: { hp: 450, speed: 85, bounty: 40 },
    // ---- The Hub · The Moon (spec: 2026-09-02-the-hub-design.md) ----------
    // Hub levels run the Ember Barrage tower-HP system with 10-HP towers, so
    // every attacker here uses `towerFire`; "passive" enemies merely walk.
    // The Cosmic Corroder: the Carrot Crusher gone purple, one purple eye and
    // a leaf blower. Blows a gust of leaves at a tower every 2s for 1 damage.
    cosmiccorroder: {
      hp: 500,
      speed: 80,
      bounty: 400,
      towerFire: { damage: 1, cooldown: 2, travel: 0.9, projectile: 'proj-leaf' },
    },
    // The Nuisance Nugget: a Nugget Trooper in a black top hat and gold
    // monocle. Throws a piece of nugget at a tower every 3s for 2 damage.
    nuisancenugget: {
      hp: 1000,
      speed: 90,
      bounty: 400,
      towerFire: { damage: 2, cooldown: 3, travel: 0.9, projectile: 'proj-nuggetpiece' },
    },
    // The Bar Waiter: a Brocco with one purple eye carrying two wine glasses.
    // Passive — no Veggie Hex, no fireballs — but a fat wall of HP.
    barwaiter: { hp: 1250, speed: 80, bounty: 450 },
    // The Burnt Fry: a Fry Swarm charred black from the middle up. Passive;
    // HTD Exhibition musters it in wave 14 only.
    burntfry: { hp: 1750, speed: 80, bounty: 350 },
    // ---- The Moon, levels 3–5 (spec Revision 4) ---------------------------
    // The Microwaved Nugget: a Nugget Trooper straight out of the microwave,
    // glowing red from the middle up. Throws a hot nugget piece at a tower
    // every 1.5s for 1 damage. While a Microwave is on the field it carries
    // 1500 HP and throws every 1s instead (see the Microwave's `empower`).
    // Cosmic Quarters musters it from wave 11.
    microwavednugget: {
      hp: 1200,
      speed: 85,
      bounty: 500,
      towerFire: { damage: 1, cooldown: 1.5, travel: 0.9, projectile: 'proj-hotnugget' },
    },
    // The 1.99 Combo: two nuggets riding a white-and-orange nugget box.
    // Passive — a fat wall of HP. Cosmic Quarters musters it from wave 7.
    combo199: { hp: 1800, speed: 80, bounty: 550 },
    // Chiply the Chip: a very angry potato chip with no hands. Shoots onion
    // rings at a tower every 2s for 2 damage, and a LEAK takes five lives —
    // every life the Gazing Dome has, so he ends the run on the spot. HP is
    // not in the brief; 2500 puts him above the Combo. Three of him, ever.
    chiply: {
      hp: 2500,
      speed: 90,
      bounty: 750,
      livesCost: 5,
      towerFire: { damage: 2, cooldown: 2, travel: 0.9, projectile: 'proj-onionring' },
    },
    // The Microwave: a big legless microwave oven with a menacing blue pixel
    // face, hopping along the trail dragging its cord (render-only). Passive
    // itself, but while
    // one is alive every Microwaved Nugget has 1500 HP and throws every 1s.
    // The Space Station sends it on waves 5, 10 and 15 only.
    microwave: {
      hp: 2000,
      speed: 75,
      bounty: 1000,
      // Four buffs, one carrier. The hot Nugget and the Spiced Nacho get HP;
      // the Moon and Laser Nachos only a faster trigger (spec
      // 2026-09-10-nacho-stages-design.md §4).
      empower: [
        { enemyType: 'microwavednugget', hp: 1500, towerFireCooldown: 1 },
        { enemyType: 'spicednacho', hp: 5500, stunDuration: 4 },
        { enemyType: 'moonnacho', towerFireCooldown: 1 },
        { enemyType: 'lasernacho', towerFireCooldown: 1 },
      ],
    },
    // CHIPLY'S DAD — The Moon's boss: a floating chip in a crown (with a chip
    // set in it) dripping salt on the trail. Must be KILLED — a leak drains
    // every remaining life, like every other boss — and cannot be slowed.
    // Every 10s he drops a chip somewhere on the playfield; unpressed for 5s
    // it costs TWO lives (see core/chip.ts). Bounty is not in the brief; 2000
    // scales King Ember's 1200 to twice his HP.
    chiplysdad: {
      hp: 60000,
      speed: 70,
      bounty: 2000,
      livesCost: 99,
      slowImmune: true,
      chipDrop: { cooldown: 10, fuse: 5, lives: 2 },
    },
    // ── Constellation Valley and Bun Beach (spec 2026-09-09-moon-stages) ──
    // NACHO — a plain triangular tortilla chip with a very angry face, no
    // arms, no hands. Passive: it only walks, fast and fat. Constellation
    // Valley from wave 10, Bun Beach from wave 5.
    nacho: { hp: 3000, speed: 95, bounty: 500 },
    // SEASONEZ CHIPPINGTON — Chiply under salt and pepper in a bucket hat.
    // Throws an onion ring at a random tower every 2s for 3 HP (Chiply's
    // cadence, a harder hit), and a leak of his costs TWO lives, not the
    // run. Constellation Valley from wave 13, Bun Beach from wave 6.
    seasonezchippington: {
      hp: 3000,
      speed: 90,
      bounty: 600,
      livesCost: 2,
      towerFire: { damage: 3, cooldown: 2, travel: 0.9, projectile: 'proj-onionring' },
    },
    // THE LIFEGUARD — a Cosmic Corroder in a red GUARD cap with a megaphone,
    // "LIFEGUARD!" over his head. No throw, one life on a leak; his weapon is
    // the whistle: 3s after he arrives one lands on the playfield, press it
    // within 5s or lose TWO lives (STRIKE), and the next comes 3s after the
    // last resolves — one at a time (`exclusive`). Bun Beach, waves 10 and
    // 18 only.
    lifeguard: {
      hp: 4000,
      speed: 85,
      bounty: 500,
      chipDrop: { kind: 'whistle', cooldown: 3, fuse: 5, lives: 2, exclusive: true },
    },
    // ── The nacho stages (spec 2026-09-10-nacho-stages-design.md) ─────────
    // Every one of these is a NACHO, so Space Debris jams the lot for two
    // minutes (see core/enemy NACHO_TYPES) and the Microwave buffs all three.
    // MOON NACHO — a grey tortilla chip with a moustache and a knife. Throws
    // the knife at a tower every 1.5s for 2 damage (every 1s under a
    // Microwave). Nach'yo Business and Rocket Rage.
    moonnacho: {
      hp: 4500,
      speed: 90,
      bounty: 500,
      towerFire: { damage: 2, cooldown: 1.5, travel: 0.9, projectile: 'proj-nachoknife' },
    },
    // SPICED NACHO — a red-hot tortilla chip. Throws nothing; instead he
    // STUNS, locking one random tower every 2s for 3s (4s under a Microwave,
    // which also takes him to 5500 HP). The clock is per-enemy, so a pair of
    // them locks two towers at a time. Nach'yo Business and Rocket Rage.
    spicednacho: {
      hp: 5000,
      speed: 90,
      bounty: 700,
      stun: { duration: 3, cooldown: 2 },
    },
    // LASER NACHO — a tortilla chip in sunglasses behind a minigun. Fires a
    // BEAM at a tower every 2s for 3 damage (every 1s under a Microwave); the
    // shot is drawn as a line, not a thrown object. Rocket Rage only.
    lasernacho: {
      hp: 4000,
      speed: 85,
      bounty: 600,
      towerFire: { damage: 3, cooldown: 2, travel: 0.35, beam: true },
    },
    // ── Through Space and Time (spec 2026-09-11-through-space-and-time) ───
    // PRINCE NACHO — a floating nacho in a red cape and a golden crown set
    // with purple gems, holding a golden staff with a red orb on top. The
    // chapter finale, wave 30.
    //
    // He is a nacho by name and by sprite but is DELIBERATELY not in
    // NACHO_TYPES: Space Debris is a stored consumable and must not be able
    // to switch the finale's boss off for two of its minutes.
    //
    // FORM ONE walks the track. Every 3s he swings his staff and a golden
    // spear falls from the top of the screen onto one random tower for 4
    // damage. A leak costs FIVE lives — at five starting lives that is the
    // run, which is the point: he must not be allowed to simply stroll past.
    princenacho: {
      hp: 75000,
      speed: 80,
      bounty: 1000,
      livesCost: 5,
      slowImmune: true,
      towerFire: {
        damage: 4,
        cooldown: 3,
        travel: 0.8,
        projectile: 'proj-goldenspear',
        fromSky: true,
      },
      // Killed (never leaked), he lands again bigger, in front of the track at
      // the top of the screen — the empty pocket the level's route leaves.
      transformsInto: { enemyType: 'princenacho2', at: { x: 640, y: 270 } },
    },
    // FORM TWO does not move and cannot be reached by the exit: he floats and
    // every tower on the board can hit him whatever its range. 100 000 HP.
    //   · every 5s  — a huge spear falls on THREE random towers, 3 damage each
    //   · every 10s — a laser sweeps the map and seals placement for 3s
    //   · below half health that laser becomes THREE lasers 3s apart, each
    //     dropping a shield to hold for 2s within 4s or lose 2 lives; his
    //     spear clock is frozen for the whole barrage.
    princenacho2: {
      hp: 100000,
      speed: 0,
      bounty: 0,
      slowImmune: true,
      anchored: true,
      ignoreRange: true,
      towerFire: {
        damage: 3,
        cooldown: 5,
        travel: 0.9,
        count: 3,
        projectile: 'proj-goldenspear',
        fromSky: true,
        // "a HUGE spear falls from the sky" — the same art, drawn big.
        scale: 1.9,
      },
      placementLock: { duration: 3, cooldown: 10 },
      laserBarrage: { count: 3, gap: 3, belowHpFraction: 0.5 },
    },
    // ---- The Origin Event · The Dark Shadows (spec 2026-10-06) ------------
    // Every stat is the brief's. Tower HP is on (10, as on The Moon), so all
    // three throw at towers. The brief named no shot art: the Ghosts throw a
    // spirit wisp, The Dead a red sword-slash.
    // The Ghost: a white, mostly see-through ghost with no legs or hands
    // (drawn translucent by EnemyView's ENEMY_ALPHA). 1 damage every 4s.
    ghost: {
      hp: 600,
      speed: 50,
      bounty: 200,
      towerFire: { damage: 1, cooldown: 4, travel: 0.9, projectile: 'proj-ghostwisp' },
    },
    // The Dead: a Carrot Crusher gone blood red, red eyes. 2 damage every 8s.
    thedead: {
      hp: 800,
      speed: 65,
      bounty: 300,
      towerFire: { damage: 2, cooldown: 8, travel: 0.9, projectile: 'proj-deadslash' },
    },
    // The Dark Ghost: the Ghost, but red. The Dark Jungle's newcomer.
    // 2 damage every 5s.
    darkghost: {
      hp: 1000,
      speed: 60,
      bounty: 500,
      towerFire: { damage: 2, cooldown: 5, travel: 0.9, projectile: 'proj-darkwisp' },
    },
    // ---- The Dark Shadows, levels 3–5 (spec 2026-10-07) --------------------
    // Every stat is the brief's.
    // The Dark Nacho: a dark red nacho, drawn a size up (EnemyView). 2 damage
    // every 3s, and a 4s stun on one tower every 5s. A NACHO_TYPE, so Space
    // Debris jams both.
    darknacho: {
      hp: 1200,
      speed: 60,
      bounty: 550,
      towerFire: { damage: 2, cooldown: 3, travel: 0.9, projectile: 'proj-darkchip' },
      stun: { duration: 4, cooldown: 5 },
    },
    // Jack'O Ghost: an orange ghost in a pumpkin. Every 4.5s he GHOSTS a
    // tower — it turns purple, stops fighting, and 1.5s later hits one other
    // tower for 3, once.
    jackoghost: {
      hp: 1500,
      speed: 65,
      bounty: 600,
      ghosting: { cooldown: 4.5, hits: 1, damage: 3, first: 1.5, gap: 0 },
    },
    // The Blue Ghost: the Ghost in light blue. Ghosting every 5s, held longer:
    // the tower hits TWO different towers for 2 each, 1.5s and 3.5s in.
    blueghost: {
      hp: 1750,
      speed: 65,
      bounty: 750,
      ghosting: { cooldown: 5, hits: 2, damage: 2, first: 1.5, gap: 2 },
    },
    // The Shadow Reaper: The Quest for the Evil Orb's boss, wave 20. One form:
    // he comes down from the top of the screen like Prince Nacho's second and
    // floats there (anchored, every tower reaches him). Every 10s he swings his
    // scythe and EVERY tower takes 2.
    reaper: {
      hp: 130000,
      speed: 0,
      bounty: 0,
      slowImmune: true,
      anchored: true,
      ignoreRange: true,
      // Under the boss bar (y 82–110), above the dead tree that holds the orb.
      anchorAt: { x: 640, y: 220 },
      towerFire: { damage: 2, cooldown: 10, travel: 0.6, everyTower: true, swing: true },
    },
    // ---- The Origin Event · Above the Clouds (spec 2026-10-08) -------------
    // Every stat is the brief's. Tower HP is on (10), as on every Origin level.
    // Winger: a Carrot Crusher with wings. Passive — he only flies the track.
    winger: { hp: 800, speed: 70, bounty: 300 },
    // Twilight: a glowing yellow ball with two eyes and wings. 1 damage every 2s.
    twilight: {
      hp: 1000,
      speed: 70,
      bounty: 400,
      towerFire: { damage: 1, cooldown: 2, travel: 0.9, projectile: 'proj-twilightspark' },
    },
    // Nightfly: Twilight in black, white eyes. 2 damage every 2.5s.
    nightfly: {
      hp: 1200,
      speed: 70,
      bounty: 500,
      towerFire: { damage: 2, cooldown: 2.5, travel: 0.9, projectile: 'proj-nightspark' },
    },
    // Halolight: a hollow Twilight under a yellow halo. Never hurts a tower
    // (the user's call); killed, he splits into two halves of 600 HP ($500
    // each), and each half into two quarters of 300 HP ($250 each).
    halolight: {
      hp: 1600,
      speed: 70,
      bounty: 1000,
      splitsInto: { enemyType: 'halolight2', count: 2, spread: 32 },
    },
    halolight2: {
      hp: 600,
      speed: 70,
      bounty: 500,
      splitsInto: { enemyType: 'halolight3', count: 2, spread: 16 },
    },
    halolight3: { hp: 300, speed: 70, bounty: 250 },
    // Lord Turtleshell: Heaven's Peak's boss (spec 2026-10-12), ALONE on wave
    // 20. A huge turtle with angel wings who floats up out of the sea at the
    // bottom of the map and hangs there (anchored, every tower reaches him).
    // Every 15 s a winged turtle drops from the sky onto each of 2 random
    // towers for 3.
    lordturtleshell: {
      hp: 120000,
      speed: 0,
      bounty: 0,
      slowImmune: true,
      anchored: true,
      ignoreRange: true,
      // Over the open sea, between the two floating islands.
      anchorAt: { x: 640, y: 612 },
      towerFire: {
        damage: 3,
        cooldown: 15,
        travel: 1,
        count: 2,
        projectile: 'proj-wingedturtle',
        fromSky: true,
      },
    },
    // ── The Fungi Forest (spec 2026-10-20-fungi-forest-design.md) ──────────
    // Three plain mushrooms with two black eyes and no limbs, who hop along
    // the trail the Microwave's way (render-only, EnemyView HOP_TYPES).
    // REDSHROOM — red cap. Spits a red spore at a random tower every 2 s for 2.
    redshroom: {
      hp: 1000,
      speed: 85,
      bounty: 400,
      towerFire: { damage: 2, cooldown: 2, travel: 0.9, projectile: 'proj-redspore' },
    },
    // POISON SHROOM — the Redshroom with a green cap. Its only attack: every
    // 6 s a poison blob at a random tower, which then loses 1 HP a second for
    // 2 seconds (the impact itself deals nothing; see core/towerPoison).
    poisonshroom: {
      hp: 1200,
      speed: 80,
      bounty: 450,
      towerFire: {
        damage: 0,
        cooldown: 6,
        travel: 0.9,
        projectile: 'proj-poisonspore',
        poison: { damage: 1, ticks: 2, every: 1 },
      },
    },
    // SKULLSHROOM — the Redshroom with a black cap and a skull on it. Every
    // 5 s a skull spore at a random tower for 3. Village of Veg onwards.
    skullshroom: {
      hp: 1500,
      speed: 80,
      bounty: 500,
      towerFire: { damage: 3, cooldown: 5, travel: 0.9, projectile: 'proj-skullspore' },
    },
    // ── Levels 4–6 (spec 2026-10-22-fungi-forest-levels-4-6-design.md) ────
    // PURPLE FUNGI — the Redshroom with a purple cap. No attack on towers:
    // every 5 s he SEALS tower placement map-wide for 2 s (Emperor Pepro's
    // Imperial Decree, the "⛔ SEALED" banner). Poison Turf onwards.
    purplefungi: {
      hp: 1550,
      speed: 80,
      bounty: 500,
      placementLock: { duration: 2, cooldown: 5 },
    },
    // THE MUSHY — Mushy Mayhem's boss, wave 23, alone: a bigger Redshroom
    // with a brown cap. 20% of tower hits MISS him ("DODGE" over his head;
    // core/enemy rollDodge), and every 4 s he throws a mushroom at a random
    // tower for 3. A leak ends the run, like every boss. HP, speed and bounty
    // are the user's pick of the offered options (80 000 / 50 / 2500).
    themushy: {
      hp: 80000,
      speed: 50,
      bounty: 2500,
      livesCost: 99,
      slowImmune: true,
      dodge: { chance: 0.2 },
      towerFire: { damage: 3, cooldown: 4, travel: 0.9, projectile: 'proj-mushyshroom' },
    },
    // GLOWSHROOM — the Redshroom with a glowing orange cap. Every 6 s he
    // throws a potion at a random tower that SHROOMNITIZES it: the Blue
    // Ghost's possession, for ONE strike of 2 at another tower, 1.5 s after
    // the potion lands. Glowing Forest.
    glowshroom: {
      hp: 2000,
      speed: 70,
      bounty: 600,
      towerFire: {
        damage: 0,
        cooldown: 6,
        travel: 0.9,
        projectile: 'proj-glowpotion',
        possess: { hits: 1, damage: 2, first: 1.5, gap: 2, projectile: 'proj-glowspore' },
      },
    },
    // ── Level 7 (spec 2026-10-24-fungi-forest-level-7-and-items-design.md) ──
    // BLIZZARD FUNGI — the Redshroom covered in ice and snow. Every 3 s he
    // throws a snowball at a random tower for 3, and 10% of tower hits MISS
    // him (The Mushy's dodge, at half the chance). Icy Photosynthesis.
    blizzardfungi: {
      hp: 1500,
      speed: 70,
      bounty: 600,
      dodge: { chance: 0.1 },
      towerFire: { damage: 3, cooldown: 3, travel: 0.9, projectile: 'proj-snowball' },
    },
    // ── Levels 8–11 (spec 2026-10-27-fungi-forest-finale-design.md) ────────
    // ZOMBIESHROOM — a dead, dark green shroom with a knife. He can only stab
    // a tower within 100 px of him: 3 every 2 s, a charged stab HELD until one
    // is in reach. Grave of the Rotten onwards.
    zombieshroom: {
      hp: 2000,
      speed: 65,
      bounty: 650,
      towerFire: {
        damage: 3,
        cooldown: 2,
        travel: 0.4,
        projectile: 'proj-nachoknife',
        range: 100,
      },
    },
    // HYNO-FUNGI — the Glowshroom glowing blue. Every 5 s it hypnotises 1–2
    // towers, each at equal odds: stunned 3 s; shroomnitized (one hit of 2 on
    // another tower); range −40% for 5 s; or stunned 3 s AND placement sealed
    // 3 s. Three in Magical Mayhem; King Shroometron calls one more.
    hynofungi: {
      hp: 2200,
      speed: 70,
      bounty: 800,
      hypnotize: {
        cooldown: 5,
        maxTowers: 2,
        stun: 3,
        seal: 3,
        rangeMult: 0.6,
        rangeFor: 5,
        possess: { hits: 1, damage: 2, first: 1.5, gap: 2, projectile: 'proj-glowspore' },
      },
    },
    // KING SHROOMETRON — the Fungi Forest's final boss (The Shroombellion,
    // wave 30, alone). Half black, half brown, crowned. A Glowshroom AND a
    // Poison Shroom at his side every 10 s; at 75% and at 50% HP four
    // mushrooms fall from the sky (tap them, or each one that lands hits a
    // tower for 5); at a quarter health he calls a Hyno-Fungi. A leak ends the
    // run, like every boss. Bounty assumed (the brief gave none).
    kingshroometron: {
      hp: 160000,
      speed: 60,
      bounty: 5000,
      livesCost: 99,
      summon: {
        enemyType: 'glowshroom',
        also: ['poisonshroom'],
        count: 1,
        cooldown: 10,
        maxAlive: 6,
      },
      hpSummon: { belowHpFraction: 0.25, enemyType: 'hynofungi', count: 1 },
      skyDrop: { atHpFractions: [0.75, 0.5], count: 4, gap: 1, fuse: 4, towerDamage: 5 },
    },
  },
};
